import { type DragEvent, type FormEvent, useDeferredValue, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, FileImage, LibraryBig, LoaderCircle, Pencil, Plus, Quote, RotateCcw, Search, Sparkles, Trash2, UploadCloud } from "lucide-react";
import type { JobStatus, RecognitionMode, WordSense } from "@preptrack/contracts";

import { Button, EmptyState, Field, Input, PageHeader, Tag, Textarea } from "@/components/primitives";
import { ReadingEntryTabs } from "@/components/reading-entry-tabs";
import { ResetPageDialog } from "@/components/reset-page-dialog";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/components/auth-context";
import { completedSenseDrafts, emptySenseDraft, senseDraftsFromSenses, SenseRowsEditor, type SenseDraft } from "@/components/sense-rows-editor";
import { WordSenses } from "@/components/word-senses";

const ACCEPTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const recognitionJobStorageKey = (userId: string) => `preptrack:recognition-job:${userId}`;

interface Candidate {
  id: string;
  wordId: string | null;
  headword: string;
  sourceSentence: string | null;
  confidence: number;
  selected: boolean;
  examSource: string | null;
  senses: SenseDraft[];
  memberships: Array<{ code: string; label: string; kind: string }>;
}
interface JobResponse {
  job: { id: string; mode: RecognitionMode; status: JobStatus; errorMessage?: string | null };
  progress: { completedSegments: number; totalSegments: number; candidateCount: number };
  candidates: Candidate[];
}
interface Suggestion {
  id: string;
  headword: string;
  senses: WordSense[];
}
interface ExamSentence {
  id: string;
  content: string;
  year: number;
  examType: string;
  section: string;
  label: string | null;
}

export function CapturePage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const storageKey = user ? recognitionJobStorageKey(user.userId) : null;
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<RecognitionMode>("marked");
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState("");
  const [jobId, setJobId] = useState<string | null>(() => storageKey ? window.localStorage.getItem(storageKey) : null);
  const [uploading, setUploading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [confirmed, setConfirmed] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [resetting, setResetting] = useState(false);
  const [addHeadword, setAddHeadword] = useState("");
  const [addSenses, setAddSenses] = useState<SenseDraft[]>([emptySenseDraft()]);
  const [addSentence, setAddSentence] = useState("");
  const [selectedExamSentence, setSelectedExamSentence] = useState<ExamSentence | null>(null);
  const [useAiCompletion, setUseAiCompletion] = useState(true);
  const [addingCandidate, setAddingCandidate] = useState(false);
  const publicConfig = useQuery({
    queryKey: ["public", "config"],
    queryFn: () => apiFetch<{ uploadLimits: { recognitionMaxMb: number } }>("/public/config"),
    staleTime: 60_000,
  });
  const recognitionMaxMb = publicConfig.data?.uploadLimits.recognitionMaxMb ?? 12;
  const deferredAddHeadword = useDeferredValue(addHeadword.trim().toLowerCase());
  const deferredAddSentence = useDeferredValue(addSentence.trim());
  const hasPageState = Boolean(file || jobId || candidates.length || confirmed !== null);

  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!storageKey) return;
    if (jobId && confirmed === null) window.localStorage.setItem(storageKey, jobId);
    else window.localStorage.removeItem(storageKey);
  }, [confirmed, jobId, storageKey]);


  const applyFile = (nextFile: File | null) => {
    setJobId(null);
    setCandidates([]);
    setConfirmed(null);
    setError("");
    setFile(nextFile);
  };

  const selectFile = (nextFile: File | null) => {
    if (!nextFile) return;
    if (!ACCEPTED_IMAGE_TYPES.has(nextFile.type)) {
      setError("请选择 JPG、PNG 或 WebP 图片。");
      return;
    }
    if (nextFile.size > recognitionMaxMb * 1024 * 1024) {
      setError(`图片不能超过 ${recognitionMaxMb} MB。`);
      return;
    }
    if (hasPageState) {
      setPendingFile(nextFile);
      setResetOpen(true);
      return;
    }
    applyFile(nextFile);
  };

  const isFileDrag = (event: DragEvent) => event.dataTransfer.types.includes("Files");

  const handleDragEnter = (event: DragEvent<HTMLButtonElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    selectFile(event.dataTransfer.files.item(0));
  };

  const job = useQuery({
    queryKey: ["recognition-job", jobId],
    queryFn: () => apiFetch<JobResponse>(`/recognition-jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (query) => ["queued", "processing"].includes(query.state.data?.job.status ?? "") ? 1400 : false,
  });
  const addWordQuery = useQuery({
    queryKey: ["suggest", "recognition-manual", deferredAddHeadword],
    queryFn: () => apiFetch<{ suggestions: Suggestion[] }>(`/words/suggest?q=${encodeURIComponent(deferredAddHeadword)}`),
    enabled: job.data?.job.status === "review_required" && deferredAddHeadword.length > 0,
    staleTime: 30_000,
  });
  const addWordSuggestions = addWordQuery.data?.suggestions ?? [];
  const exactAddWord = addWordSuggestions.find((item) => item.headword.toLowerCase() === deferredAddHeadword);
  const bestAddWord = exactAddWord ?? addWordSuggestions[0];
  const addSentenceLookup = deferredAddSentence || bestAddWord?.headword || deferredAddHeadword;
  const addSentenceQuery = useQuery({
    queryKey: ["exam-sentences", "recognition-manual", addSentenceLookup, deferredAddHeadword],
    queryFn: () => apiFetch<{ sentences: ExamSentence[] }>(`/exam-sentences/search?q=${encodeURIComponent(addSentenceLookup)}&headword=${encodeURIComponent(bestAddWord?.headword ?? deferredAddHeadword)}`),
    enabled: job.data?.job.status === "review_required" && addSentenceLookup.length >= 2 && !selectedExamSentence,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (!job.data) return;
    if (job.data.job.status === "completed") {
      if (storageKey) window.localStorage.removeItem(storageKey);
      setJobId(null);
      setCandidates([]);
      return;
    }
    setMode(job.data.job.mode);
    setCandidates(job.data.candidates);
  }, [job.data, storageKey]);

  useEffect(() => {
    if (jobId && job.isError) {
      setError("上次未保存的识别任务暂时无法加载，请稍后重试或重置本页。");
    }
  }, [job.isError, jobId]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    setCandidates([]);
    setConfirmed(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const result = await apiFetch<{ jobId: string }>(`/recognition-jobs?mode=${mode}`, { method: "POST", body });
      setJobId(result.jobId);
      if (storageKey) window.localStorage.setItem(storageKey, result.jobId);
    } catch {
      setError("上传失败。请确认图片格式、尺寸和 API 服务状态。");
    } finally {
      setUploading(false);
    }
  };

  const update = async (id: string, patch: Partial<Pick<Candidate, "headword" | "sourceSentence" | "selected" | "senses">>) => {
    setCandidates((current) => current.map((candidate) => candidate.id === id ? { ...candidate, ...patch } : candidate));
    if (!jobId) return;
    try {
      await apiFetch(`/recognition-jobs/${jobId}/candidates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
    } catch {
      setError("候选修改未能保存，请重试。");
      await job.refetch();
    }
  };

  const updateAllSelected = async (selected: boolean) => {
    setCandidates((current) => current.map((candidate) => ({ ...candidate, selected })));
    if (!jobId) return;
    try {
      await apiFetch(`/recognition-jobs/${jobId}/candidates`, {
        method: "PATCH",
        body: JSON.stringify({ selected }),
      });
    } catch {
      setError("批量选择未能保存，请重试。");
      await job.refetch();
    }
  };

  const remove = async (candidate: Candidate) => {
    await update(candidate.id, { selected: false });
    setCandidates((items) => items.filter((item) => item.id !== candidate.id));
  };

  const confirm = async () => {
    if (!jobId) return;
    setError("");
    try {
      const result = await apiFetch<{ saved: number }>(`/recognition-jobs/${jobId}/confirm`, { method: "POST" });
      setConfirmed(result.saved);
      if (storageKey) window.localStorage.removeItem(storageKey);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user-words"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["word-books"] }),
      ]);
    } catch {
      setError("保存失败，请确认任务已完成识别并重试。");
    }
  };
  const addCandidate = async (event: FormEvent) => {
    event.preventDefault();
    if (!jobId || !addHeadword.trim()) return;
    setAddingCandidate(true);
    setError("");
    try {
      await apiFetch(`/recognition-jobs/${jobId}/candidates`, {
        method: "POST",
        body: JSON.stringify({
          headword: addHeadword,
          senses: completedSenseDrafts(addSenses),
          sourceSentence: addSentence || undefined,
          examSentenceId: selectedExamSentence?.id,
          useAiCompletion,
        }),
      });
      setAddHeadword("");
      setAddSenses([emptySenseDraft()]);
      setAddSentence("");
      setSelectedExamSentence(null);
      await job.refetch();
    } catch {
      setError("补录失败，请检查英文、释义和 MiMo 服务后重试。");
    } finally {
      setAddingCandidate(false);
    }
  };
  const resetPage = async () => {
    setResetting(true);
    setError("");
    try {
      if (jobId) {
        await apiFetch(`/recognition-jobs/${jobId}`, { method: "DELETE" });
        queryClient.removeQueries({ queryKey: ["recognition-job", jobId] });
      }
      if (storageKey) window.localStorage.removeItem(storageKey);
      const replacement = pendingFile;
      setFile(replacement);
      setIsDragging(false);
      dragDepth.current = 0;
      setJobId(null);
      setCandidates([]);
      setConfirmed(null);
      setAddHeadword("");
      setAddSenses([emptySenseDraft()]);
      setAddSentence("");
      setSelectedExamSentence(null);
      setUseAiCompletion(true);
      setPendingFile(null);
      setResetOpen(false);
      if (inputRef.current) inputRef.current.value = "";
    } catch {
      setError("当前识别任务未能清理，请稍后重试。页面数据已保留。");
    } finally {
      setResetting(false);
    }
  };

  const selectedCount = candidates.filter((candidate) => candidate.selected).length;
  const status = job.data?.job.status;
  const isRestoring = Boolean(jobId) && job.isLoading;
  const isRecognizing = status === "queued" || status === "processing";
  const showRecognitionProgress = isRestoring || isRecognizing;
  const modeLabel = mode === "marked" ? "红笔标记" : "整页词汇";
  const progress = job.data?.progress;
  const progressText = progress?.totalSegments
    ? `已完成 ${progress.completedSegments}/${progress.totalSegments} 个分区，当前识别 ${progress.candidateCount} 个候选`
    : "正在分析图片并规划识别分区";

  return (
    <div className="page capture-page">
      <PageHeader
        title="阅读生词"
        description="记录阅读时遇到的词，选择手动录入或拍照识别，确认后再保存到个人词库。"
        action={<div className="page-heading-actions"><Link to="/app/$subject/word-library" params={{ subject: "english" }} className="button button--secondary button--md"><LibraryBig /> 管理词库</Link><Button type="button" variant="ghost" onClick={() => { setPendingFile(null); setResetOpen(true); }} disabled={!hasPageState}><RotateCcw /> 重置本页</Button></div>}
      />
      <ReadingEntryTabs active="capture" />

      <section className="capture-workspace">
        <aside className="paper-panel">
          <div className="section-heading-row"><h2>识别来源</h2><span>{file?.name ?? "尚未选择图片"}</span></div>
          <fieldset className="recognition-mode-picker" disabled={Boolean(jobId) && confirmed === null}>
            <legend>识别模式</legend>
            <label className={mode === "marked" ? "recognition-mode-option recognition-mode-option--active" : "recognition-mode-option"}>
              <input type="radio" name="recognition-mode" value="marked" checked={mode === "marked"} onChange={() => setMode("marked")} />
              <span><strong>红笔标记</strong><small>仅识别红圈、红线标出的英文词</small></span>
            </label>
            <label className={mode === "all_words" ? "recognition-mode-option recognition-mode-option--active" : "recognition-mode-option"}>
              <input type="radio" name="recognition-mode" value="all_words" checked={mode === "all_words"} onChange={() => setMode("all_words")} />
              <span><strong>整页词汇</strong><small>识别页面中全部英文词汇条目与短语</small></span>
            </label>
          </fieldset>
          <button
            className={"paper-preview" + (isDragging ? " paper-preview--dragging" : "")}
            type="button"
            aria-label={preview ? "更换试卷照片" : "选择或拖放试卷照片"}
            onClick={() => inputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {preview ? <img src={preview} alt="待识别的试卷页面" /> : (
              <span className="upload-placeholder">
                <UploadCloud />
                <strong>拖放试卷照片</strong>
                <small>或点击选择文件</small>
              </span>
            )}
            {preview ? <span className="paper-preview__replace"><UploadCloud /> 点击或拖放以更换</span> : null}
            {isDragging ? <span className="paper-preview__drop"><UploadCloud /> 松开以选择图片</span> : null}
          </button>
          <input ref={inputRef} className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { selectFile(event.target.files?.[0] ?? null); event.currentTarget.value = ""; }} />
          <div className="paper-meta"><FileImage /><span>支持 JPG、PNG、WebP<br />最大 12MB，上传时自动清除 EXIF</span></div>
          <Button variant="primary" onClick={upload} disabled={uploading || showRecognitionProgress || !file}>{uploading || showRecognitionProgress ? <><LoaderCircle className="recognition-spinner recognition-spinner--button" /> {uploading ? "正在上传…" : isRestoring ? "正在恢复结果…" : `正在${modeLabel}识别…`}</> : `开始${modeLabel}识别`}</Button>
        </aside>

        <div className="candidate-panel">
          <div className="candidate-panel__head">
            <div><p className="section-label">识别结果</p><h2>{showRecognitionProgress ? progress?.candidateCount ?? 0 : candidates.length} 个候选词</h2></div>
            <p>{showRecognitionProgress ? "任务正在后台识别，请稍候。" : "请逐项检查。未选择的词不会保存。"}</p>
          </div>
          {showRecognitionProgress ? (
            <div className="recognition-progress" role="status" aria-live="polite">
              <LoaderCircle className="recognition-spinner recognition-spinner--large" aria-hidden="true" />
              <strong>{isRestoring ? "正在恢复识别结果" : status === "queued" ? `${modeLabel}识别任务已排队` : mode === "marked" ? "正在识别红笔圈词" : "正在识别整页词汇"}</strong>
              <span>{isRestoring ? "正在读取上次未保存的任务" : progressText}</span>
            </div>
          ) : candidates.length === 0 ? (
            <EmptyState title="等待识别结果" description={`选择一张图片，并使用${modeLabel}模式开始识别。`} />
          ) : (
            <div className="candidate-table" role="table" aria-label="识别候选词">
              <div className="candidate-row candidate-row--head" role="row">
                <span>选择</span><span>置信度</span><span>识别结果</span><span>可能来源</span><span>已有归属</span><span>操作</span>
              </div>
              {candidates.map((candidate) => (
                <div className="candidate-row" role="row" key={candidate.id}>
                  <label><input type="checkbox" checked={candidate.selected} onChange={(event) => void update(candidate.id, { selected: event.target.checked })} /><span className="sr-only">选择 {candidate.headword}</span></label>
                  <span className="confidence">{Math.round(candidate.confidence * 100)}%</span>
                  <input id={`candidate-word-${candidate.id}`} value={candidate.headword} onChange={(event) => setCandidates((current) => current.map((item) => item.id === candidate.id ? { ...item, headword: event.target.value } : item))} onBlur={(event) => void update(candidate.id, { headword: event.target.value })} aria-label="识别单词" />
                  <span className="source-cell">{candidate.examSource ?? "未匹配到真题"}</span>
                  <span className="tag-list candidate-memberships">{candidate.memberships.length ? candidate.memberships.map((membership) => <Tag key={membership.code} variant="metadata" tone={membership.kind === "official" ? "accent" : "neutral"}>{membership.label}</Tag>) : <Tag variant="metadata">未收录</Tag>}</span>
                  <span className="row-actions"><button className="candidate-action" type="button" onClick={() => document.getElementById(`candidate-word-${candidate.id}`)?.focus()} aria-label={`编辑 ${candidate.headword}`} title="编辑候选词"><Pencil /></button><button className="candidate-action candidate-action--danger" type="button" onClick={() => void remove(candidate)} aria-label={`删除 ${candidate.headword}`} title="删除候选词"><Trash2 /></button></span>
                  <p className="candidate-sentence">{candidate.sourceSentence || "未提取到完整来源句子"}</p>
                  <div className="candidate-senses">
                    <SenseRowsEditor
                      value={candidate.senses}
                      onChange={(senses) => setCandidates((current) => current.map((item) => item.id === candidate.id ? { ...item, senses } : item))}
                      onCommit={(senses) => {
                        const completed = completedSenseDrafts(senses);
                        if (completed.length) void update(candidate.id, { senses: completed });
                      }}
                      idPrefix={`candidate-senses-${candidate.id}`}
                      addLabel="添加词性"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {status === "review_required" && !showRecognitionProgress ? (
            <form className="candidate-add" onSubmit={addCandidate}>
              <div className="candidate-add__heading">
                <div><p className="section-label">补录漏识别词</p><h3><Plus /> 手动加入本次结果</h3></div>
                <p>输入单词或短语后会即时查询词典和真题；没有释义时可由 MiMo 补全。</p>
              </div>
              <div className="candidate-add__grid">
                <div className="candidate-add__word">
                  <Field label="英文单词或短语" htmlFor="candidate-add-headword">
                    <Input id="candidate-add-headword" required pattern="[A-Za-z][A-Za-z '-]*" value={addHeadword} onChange={(event) => { setAddHeadword(event.target.value); setSelectedExamSentence(null); }} placeholder="例如 resilient" />
                  </Field>
                  {addWordQuery.isFetching ? <small className="candidate-add__status"><LoaderCircle /> 正在匹配词典</small> : null}
                  {bestAddWord ? (
                    <button className="candidate-add__suggestion" type="button" onClick={() => { setAddHeadword(bestAddWord.headword); setAddSenses(senseDraftsFromSenses(bestAddWord.senses)); }}>
                      <span><Sparkles /> 采用词典释义 <strong>{bestAddWord.headword}</strong></span>
                      <WordSenses senses={bestAddWord.senses} variant="compact" />
                    </button>
                  ) : null}
                </div>
                <Field label="释义" htmlFor="candidate-add-sense-definition-0" optional hint="可留空；若词典也无释义则使用 MiMo。">
                  <SenseRowsEditor value={addSenses} onChange={setAddSenses} idPrefix="candidate-add-sense" addLabel="添加词性" />
                </Field>
                <div className="candidate-add__sentence">
                  <Field label="来源句子" htmlFor="candidate-add-sentence" optional>
                    <div className="input-with-icon"><Textarea id="candidate-add-sentence" value={addSentence} onChange={(event) => { setAddSentence(event.target.value); setSelectedExamSentence(null); }} placeholder="粘贴图片中的句子，或按当前词匹配真题" rows={3} /><Search /></div>
                  </Field>
                  {selectedExamSentence ? (
                    <div className="candidate-add__selected"><Quote /><span><strong>{selectedExamSentence.year} {selectedExamSentence.examType} · {selectedExamSentence.label ?? selectedExamSentence.section}</strong>{selectedExamSentence.content}</span></div>
                  ) : addSentenceQuery.data?.sentences.length ? (
                    <div className="candidate-add__matches">
                      {addSentenceQuery.data.sentences.slice(0, 3).map((sentence) => (
                        <button type="button" key={sentence.id} onClick={() => { setSelectedExamSentence(sentence); setAddSentence(sentence.content); }}>
                          <strong>{sentence.year} {sentence.examType} · {sentence.label ?? sentence.section}</strong><span>{sentence.content}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="candidate-add__actions">
                <label className="check-row"><input type="checkbox" checked={useAiCompletion} onChange={(event) => setUseAiCompletion(event.target.checked)} /><span><strong>缺少释义时用 MiMo 补全</strong><small>已有手工或词典释义时不会调用</small></span></label>
                <Button variant="secondary" type="submit" disabled={addingCandidate || !addHeadword.trim()}>{addingCandidate ? <><LoaderCircle /> 正在补录</> : <><Plus /> 加入识别结果</>}</Button>
              </div>
            </form>
          ) : null}
          {candidates.length > 0 && !showRecognitionProgress ? (
            <footer className="candidate-footer">
              <label><input type="checkbox" checked={selectedCount === candidates.length} onChange={(event) => void updateAllSelected(event.target.checked)} /> 全选（{selectedCount}/{candidates.length}）</label>
              <div><span>已选择 {selectedCount} 条</span><Button variant="primary" onClick={confirm} disabled={selectedCount === 0 || confirmed !== null || status !== "review_required"}>{confirmed !== null ? <><Check /> 已保存</> : "确认保存所选到生词本"}</Button></div>
            </footer>
          ) : null}
          {confirmed !== null ? <div className="success-strip" role="status"><Check /> 已保存 {confirmed} 个词。你可以在词库管理中继续编辑。</div> : null}
          {status === "failed" ? <div className="error-strip"><RotateCcw /> 识别失败：{job.data?.job.errorMessage}</div> : null}
          {error ? <div className="error-strip" role="alert">{error}</div> : null}
        </div>
      </section>

      <ResetPageDialog
        open={resetOpen}
        title={pendingFile ? "更换当前照片？" : "重置拍照录入？"}
        description={pendingFile
          ? "当前照片、识别任务和未保存候选将被清除，然后载入刚选择的新照片。已经保存到词库的生词不会受影响。"
          : "当前照片、识别任务和未保存候选将从页面和服务器清除。已经保存到词库的生词不会受影响。"}
        onClose={() => { setPendingFile(null); setResetOpen(false); }}
        onConfirm={resetPage}
        confirming={resetting}
        confirmLabel={pendingFile ? "确认更换" : "确认重置"}
      />
    </div>
  );
}
